import React, { useState } from 'react';
import '../styles/WhatsAppApiDoc.scss';
import { apiEndpoints } from '../constants/apiEndpointsData';

const WhatsAppApiDoc = () => {
  const [activeLanguage, setActiveLanguage] = useState('curl');
  const [activeResponses, setActiveResponses] = useState({});

  const getMethodBadgeClass = (method) => {
    const baseClass = 'whatsapp-api-doc__method-badge';
    switch (method) {
      case 'GET': return `${baseClass} ${baseClass}--get`;
      case 'POST': return `${baseClass} ${baseClass}--post`;
      case 'PUT': return `${baseClass} ${baseClass}--put`;
      case 'DELETE': return `${baseClass} ${baseClass}--delete`;
      default: return `${baseClass} ${baseClass}--default`;
    }
  };

  const getResponseCodeClass = (success) => {
    const baseClass = 'whatsapp-api-doc__response-code';
    return `${baseClass} ${baseClass}--${success ? 'success' : 'error'}`;
  };

  const getTabClass = (language) => {
    const baseClass = 'whatsapp-api-doc__tab';
    return `${baseClass} ${activeLanguage === language ? `${baseClass}--active` : ''}`;
  };

  const getResponseTabClass = (endpointId, index) => {
    const baseClass = 'whatsapp-api-doc__tab';
    return `${baseClass} ${activeResponses[endpointId] === index ? `${baseClass}--active` : ''}`;
  };

  const ExampleTabs = ({ example }) => {
    const languages = [
      { id: 'curl', name: 'cURL' },
      { id: 'javascript', name: 'JavaScript' },
      { id: 'python', name: 'Python' },
      { id: 'php', name: 'PHP' }
    ].filter(lang => example[lang.id]);

    return (
      <div className="whatsapp-api-doc__tab-container">
        <div className="whatsapp-api-doc__tab-list">
          {languages.map(lang => (
            <button
              key={lang.id}
              className={getTabClass(lang.id)}
              onClick={() => setActiveLanguage(lang.id)}
            >
              {lang.name}
            </button>
          ))}
        </div>
        <div className="whatsapp-api-doc__tab-panel">
          <pre className="whatsapp-api-doc__code-block">
            <code>{example[activeLanguage]}</code>
          </pre>
        </div>
      </div>
    );
  };

  const ResponseTabs = ({ responses, endpointId }) => {
    // Initialize the active response tab if not set
    if (activeResponses[endpointId] === undefined) {
      setActiveResponses(prev => ({ ...prev, [endpointId]: 0 }));
    }

    const responseTypes = [
      { index: 0, name: 'Success Response' },
      { index: 1, name: 'Error - Invalid Token' },
      { index: 2, name: 'Error - Invalid Parameters' }
    ];

    return (
      <div className="whatsapp-api-doc__tab-container">
        <div className="whatsapp-api-doc__tab-list">
          {responseTypes.map(type => (
            <button
              key={type.index}
              className={getResponseTabClass(endpointId, type.index)}
              onClick={() => setActiveResponses(prev => ({ ...prev, [endpointId]: type.index }))}
            >
              {type.name}
            </button>
          ))}
        </div>
        <div className="whatsapp-api-doc__tab-panel">
          <div className={getResponseCodeClass(responses[activeResponses[endpointId]]?.status)}>
            {responses[activeResponses[endpointId]]?.status ? '200 OK' : '400 Bad Request'}
          </div>
          <pre className="whatsapp-api-doc__code-block">
            <code>{JSON.stringify(responses[activeResponses[endpointId]], null, 2)}</code>
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="whatsapp-api-doc">
      <div className="whatsapp-api-doc__container">
        <div className="whatsapp-api-doc__sidebar">
          <nav>
            <ul className="whatsapp-api-doc__nav-list">
              {apiEndpoints.map(endpoint => (
                <li key={endpoint.id} className="whatsapp-api-doc__nav-item">
                  <a href={`#${endpoint.id}`}>{endpoint.path}</a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="whatsapp-api-doc__main-content">
          <h1 className="whatsapp-api-doc__api-title">WhatsApp API Documentation</h1>

          {apiEndpoints.map(endpoint => (
            <div key={endpoint.id} id={endpoint.id} className="whatsapp-api-doc__endpoint-section">
              <div>
                <span className={getMethodBadgeClass(endpoint.method)}>{endpoint.method}</span>
                <code className="whatsapp-api-doc__endpoint-url">{endpoint.path}</code>
              </div>

              <p className="whatsapp-api-doc__description">{endpoint.description}</p>

              <h2 className="whatsapp-api-doc__section-title">Parameters</h2>
              <table className="whatsapp-api-doc__table">
                <thead>
                  <tr>
                    <th className="whatsapp-api-doc__th">Name</th>
                    <th className="whatsapp-api-doc__th">Type</th>
                    <th className="whatsapp-api-doc__th">Required</th>
                    <th className="whatsapp-api-doc__th">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.parameters.map(param => (
                    <tr key={param.name}>
                      <td className="whatsapp-api-doc__td">{param.name}</td>
                      <td className="whatsapp-api-doc__td">{param.type}</td>
                      <td className="whatsapp-api-doc__td">{param.required ? 'Yes' : 'No'}</td>
                      <td className="whatsapp-api-doc__td">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2 className="whatsapp-api-doc__section-title">Example Request</h2>
              <ExampleTabs example={endpoint.example} />

              <h2 className="whatsapp-api-doc__section-title">Response</h2>
              <ResponseTabs responses={endpoint.responses} endpointId={endpoint.id} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppApiDoc;